import {BaseComponent} from '../base/component.base.js';
import template from "./client-detail.template.html";
import styles from "./client-detail.styles.css";
import {
    inferTemplateLayoutContexts,
    isReservedAutoInjectedTemplateFieldKey,
    sanitizeTemplateFields,
    toLayoutEditorState,
    toTemplateLayoutConfig,
    formatCompositionJson,
    formatCompositionText,
    parseCompositionJson,
    parseColumnOptions,
    toTemplateFieldPayload,
    validateTableFieldConfig,
} from '../../core/client/client.core.js';
import {
    checkboxFieldOptions,
    detectedFieldConfig,
    extractTemplateKeys,
    MANUAL_FIELD_PRESETS,
    manualFieldConfig,
    presetFieldConfig,
    selectableCheckboxField,
    toFieldConfig,
    validateManualFieldKey,
} from '../../core/client/template-fields.core.js';
import {
    findDuplicateBreadcrumbOverride,
    toBreadcrumbConfig,
    toBreadcrumbOverrides,
} from '../../core/client/breadcrumb-overrides.core.js';
import {
    DEFAULT_RENDER_MODE,
    FIELD_TYPE,
    RENDER_MODE,
    SLUG_FIELD_KEY,
} from '../../core/vocabulary.js';
import {escapeHtml} from '../../core/escape-html.js';
import {fieldRowMarkup, readFieldRow, syncFieldRowTypeControls} from './field-row.view.js';
import {
    clearTableFieldErrors,
    emptyTableColumn,
    renderTableEditor,
    syncColumnTypeControls,
    tableFieldInput,
} from './table-column-editor.view.js';
import {indexedByFieldOptionsMarkup, renderLayoutContextEditors} from './layout-context-editor.view.js';
import {
    breadcrumbOverridesMarkup,
    readBreadcrumbOverrides,
    syncBreadcrumbOverrideRemoveLabel,
} from './breadcrumb-overrides.view.js';
import {renderTemplateList, templateMatchesFilter} from './template-list.view.js';
import {enableCodeEditing, replaceText} from './code-editor.view.js';
import '../billing-card/billing-card.component.js';
import '../one-off-incomes/one-off-incomes.component.js';
import {confirmDiscard} from '../confirm-dialog/confirm-discard.js';

/** Option value of the `#manual-field-preset` select that adds a single field by key. */
const CUSTOM_FIELD_PRESET = 'custom';
/** What the loading overlay says unless a handler sets `loadingText` for a longer request. */
const DEFAULT_LOADING_TEXT = 'Loading...';

/**
 * Helper inputs of the template form that only prepare a field; what they hold is not part of
 * the template, so they never make the form count as changed.
 */
const SNAPSHOT_IGNORED_IDS = new Set(['manual-field-preset', 'manual-field-key']);

export class ClientDetailComponent extends BaseComponent {
    #slug = '';
    #client = null;
    /** @type {Map<string, import('../../core/client/template-fields.core.js').FieldConfig>} */
    #fieldConfigs = new Map();
    #editingTemplateName = null;
    #layoutState = toLayoutEditorState(null);
    #editingIndexedByField = '';
    #breadcrumbOverrides = [];
    #nextBreadcrumbOverrideId = 1;
    /** Template form values as last loaded or saved; see `hasUnsavedChanges`. */
    #savedFormSnapshot = null;

    /** Asks the browser to confirm closing or reloading the tab while the template form has unsaved changes. */
    #onBeforeUnload = (e) => {
        if (this.hasUnsavedChanges()) {
            e.preventDefault();
            e.returnValue = '';
        }
    };

    connectedCallback() {
        this.loadTemplate(template, styles);
        window.addEventListener('beforeunload', this.#onBeforeUnload);

        // Listen for user changes to sync state locally
        this.shadowRoot.addEventListener('input', (e) => this.#handleStateUpdate(e));
        this.shadowRoot.addEventListener('change', (e) => this.#handleStateUpdate(e));

        const contentArea = this.$('#template-content');
        contentArea?.addEventListener('blur', () => this.scanFields());
        enableCodeEditing(contentArea);
        enableCodeEditing(this.$('#composition-json'));
        this.$('#template-filter')?.addEventListener('input', () => this.#renderTemplateList());
        this.$('#manual-field-key')?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.#addManualField();
            }
        });
        this.$('#manual-field-preset')?.addEventListener('change', () => this.#syncManualFieldControls());
        this.$('#composition-json')?.addEventListener('input', () => this.#showCompositionError(null));
        this.$('#slug-prefix')?.addEventListener('input', () => this.#syncSlugField());
        this.$('#indexed-by-field')?.addEventListener('change', (e) => {
            this.#editingIndexedByField = e.target.value;
        });
        this.$('#homepage')?.addEventListener('change', (e) => {
            this.#applyHomepageLock(e.target.checked);
        });
        this.$('#mode')?.addEventListener('change', () => {
            this.#syncSlugPrefixVisibility();
            this.#syncSlugField();
            this.#syncHomepageVisibility();
        });

        this.$('#template-form')?.addEventListener('submit', (e) => this.#handleSubmit(e));
        this.$('#edit-client-form')?.addEventListener('submit', (e) => {
            e.preventDefault();
            this.showClientSettingsError(null);
            const form = e.target;
            const detail = {
                slug: this.#slug,
                name: form.name.value,
                repoName: form.repoName.value,
                domain: form.domain.value,
                basePath: form.basePath.value,
                userCreationGuid: form.userCreationGuid.value,
            };
            this.emit('client-update', detail);
        });

        this.shadowRoot.addEventListener('click', (e) => this.#handleClick(e));

        this.render();
        this.#syncManualFieldControls();
        this.#syncSlugPrefixVisibility();
        this.#syncHomepageVisibility();
        this.#renderFieldConfigs();
        this.#renderBreadcrumbOverrides();
        this.#markFormSaved();
    }

    disconnectedCallback() {
        window.removeEventListener('beforeunload', this.#onBeforeUnload);
    }

    /** * Reads current UI values from the DOM and saves them to our Map
     * so we don't lose user input when the list re-renders.
     */
    #syncStateFromDOM() {
        for (const row of this.$$('.field-row')) {
            const key = row.dataset.key;
            this.#fieldConfigs.set(key, readFieldRow(row, this.#fieldConfigs.get(key)));
        }
    }

    /** Parses {{keys}} and reconciles with the existing state */
    scanFields() {
        // Save what the user has already typed in the existing rows
        this.#syncStateFromDOM();
        this.#removeReservedAutoInjectedFields();

        const foundKeys = extractTemplateKeys(this.$('#template-content')?.value || '');

        for (const key of this.#fieldConfigs.keys()) {
            const existing = this.#fieldConfigs.get(key);
            if (!existing) continue;
            if (foundKeys.has(key) && existing.manual) {
                this.#fieldConfigs.set(key, {
                    ...existing,
                    manual: false
                });
            }
        }

        // Remove detected keys that no longer exist in the text.
        // Keep manually added keys.
        for (const key of this.#fieldConfigs.keys()) {
            const existing = this.#fieldConfigs.get(key);
            if (!foundKeys.has(key) && !existing?.manual) {
                this.#fieldConfigs.delete(key);
            }
        }

        // Add new keys that were just discovered
        foundKeys.forEach(key => {
            if (!this.#fieldConfigs.has(key)) {
                this.#fieldConfigs.set(key, detectedFieldConfig(key));
            }
        });

        this.#renderFieldConfigs();
    }

    #renderFieldConfigs() {
        const wrapper = this.$('#detected-fields-wrapper');
        const container = this.$('#fields-container');
        wrapper.hidden = false;

        container.innerHTML = [...this.#fieldConfigs.entries()]
            .map(([key, config]) => fieldRowMarkup(key, config))
            .join('');

        const emptyState = this.$('#fields-empty-state');
        if (emptyState) {
            emptyState.hidden = this.#fieldConfigs.size > 0;
        }
        this.#renderLayoutToggleOptions();
    }

    /** @param {HTMLElement} button a column editor button inside a table field row */
    #updateColumns(button, update) {
        this.#syncStateFromDOM();
        const row = button.closest('.field-row');
        const config = this.#fieldConfigs.get(row.dataset.key);
        config.columns = update([...config.columns], Number(button.dataset.index));
        renderTableEditor(row, config);
        clearTableFieldErrors(row);
    }

    /**
     * Checks every table field against the backend schema rules and flags the first problem on the
     * offending input, so the request is only sent when the backend can accept the columns.
     *
     * @returns {boolean} true when all table fields are valid
     */
    #validateTableFields() {
        for (const [key, config] of this.#fieldConfigs) {
            if (config.type !== FIELD_TYPE.TABLE) {
                continue;
            }
            const error = validateTableFieldConfig(key, config);
            if (error) {
                const input = tableFieldInput(this.$(`.field-row[data-key="${CSS.escape(key)}"]`), error);
                input.setCustomValidity(error.message);
                input.reportValidity();
                return false;
            }
        }
        return true;
    }

    #renderLayoutToggleOptions() {
        const checkboxFields = checkboxFieldOptions(this.#fieldConfigs);
        this.#renderIndexedByFieldOptions(checkboxFields);
        const contexts = this.#layoutContextNames();
        const {enabledByField, presentationByContext, columnsByContext} = this.#layoutState;
        for (const context of contexts) {
            enabledByField.set(context, selectableCheckboxField(checkboxFields, enabledByField.get(context) || ''));
        }
        renderLayoutContextEditors(this.$('#layout-toggle-fields'), {
            contexts,
            checkboxFields,
            enabledByField,
            presentationByContext,
            columnsByContext,
            onEnabledByFieldChange: (context, fieldKey) => this.#layoutState.enabledByField.set(context, fieldKey),
            onPresentationChange: (context, presentation) => this.#layoutState.presentationByContext.set(context, presentation),
            onColumnsChange: (context, columns) => this.#layoutState.columnsByContext.set(context, columns),
        });
    }

    /** @param {{key: string, label: string}[]} checkboxFields */
    #renderIndexedByFieldOptions(checkboxFields) {
        const select = this.$('#indexed-by-field');
        select.innerHTML = indexedByFieldOptionsMarkup(checkboxFields);
        select.value = selectableCheckboxField(checkboxFields, this.#editingIndexedByField);
        this.#editingIndexedByField = select.value;
    }

    #layoutContextNames() {
        return inferTemplateLayoutContexts(
            [...this.#fieldConfigs.entries()].map(([key, config]) => ({key, ...config})),
            [...this.#layoutState.enabledByField.keys()]
        );
    }

    #handleStateUpdate(e) {
        // Clear validation error on textarea while typing
        if (e.target.id === 'template-content') {
            e.target.setCustomValidity('');
        }
        // Update local map as user interacts
        const row = e.target.closest('.field-row');
        if (row) {
            clearTableFieldErrors(row);
            this.#syncStateFromDOM();
            const prop = e.target.dataset?.prop;
            if (prop === 'type') {
                syncFieldRowTypeControls(row, e.target.value);
                const config = this.#fieldConfigs.get(row.dataset.key);
                if (config.type === FIELD_TYPE.TABLE && config.columns.length === 0) {
                    config.columns = [emptyTableColumn()];
                }
                renderTableEditor(row, config);
                this.#renderLayoutToggleOptions();
            }
            if (e.target.dataset?.colProp === 'type') {
                syncColumnTypeControls(e.target.closest('.table-column-row'), e.target.value);
            }
            if (prop === 'label') {
                this.#renderLayoutToggleOptions();
            }
        }
        const breadcrumbRow = e.target.closest('.breadcrumb-override-row');
        if (breadcrumbRow) {
            this.#syncBreadcrumbOverridesFromDOM();
            syncBreadcrumbOverrideRemoveLabel(breadcrumbRow);
        }
    }

    #handleSubmit(e) {
        e.preventDefault();
        const form = e.target;
        const contentArea = this.$('#template-content');
        this.showTemplateError(null);

        // Reset validity
        contentArea.setCustomValidity('');

        // Final scan check (in case user pasted text and hit submit immediately)
        this.scanFields();
        this.#removeReservedAutoInjectedFields();

        const mode = this.$('#mode').value;
        if (this.#fieldConfigs.size === 0 && mode !== RENDER_MODE.INTERNAL) {
            contentArea.setCustomValidity('Template must contain at least one {{variable}}.');
            contentArea.reportValidity();
            return;
        }

        if (!this.#validateTableFields()) {
            return;
        }
        for (const row of this.$$('.field-row')) {
            const optionsInput = row.querySelector('[data-prop="options"]');
            optionsInput.setCustomValidity('');
            if (row.querySelector('[data-prop="type"]').value !== FIELD_TYPE.SELECT) continue;
            const options = parseColumnOptions(optionsInput.value);
            if (options.length === 0 || new Set(options).size !== options.length) {
                optionsInput.setCustomValidity('Add at least one unique option per line.');
                optionsInput.reportValidity();
                return;
            }
        }
        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }
        if (!this.#validateBreadcrumbOverrides()) {
            return;
        }
        const {composition, error: compositionError} = parseCompositionJson(this.$('#composition-json').value);
        if (compositionError) {
            this.#showCompositionError(compositionError);
            return;
        }

        const fields = sanitizeTemplateFields(Array.from(this.#fieldConfigs.entries())
            .map(([key, val]) => toTemplateFieldPayload(key, val)));
        const breadcrumbs = toBreadcrumbConfig(this.#breadcrumbOverrides);
        const layout = toTemplateLayoutConfig(fields, new Map(
            this.#layoutContextNames().map((context) => [context, this.$(`#layout-enabled-by-field-${context}`)?.value || ''])
        ), this.#layoutState.presentationByContext, new Map(
            this.#layoutContextNames().map((context) => [context, this.$(`#layout-columns-${context}`)?.value || ''])
        ));

        if (this.#editingTemplateName) {
            this.emit('template-update', {
                slug: this.#slug,
                templateName: this.#editingTemplateName,
                content: contentArea.value,
                description: this.$('#description').value,
                mode: this.$('#mode').value,
                singleInstance: this.$('#single-instance').checked,
                homepage: this.$('#homepage').checked,
                slugPrefix: this.$('#slug-prefix').value,
                layout,
                breadcrumbs,
                indexedByField: this.$('#indexed-by-field').value,
                composition,
                fields,
            });
        } else {
            this.emit('template-add', {
                slug: this.#slug,
                name: this.$('#name').value,
                content: contentArea.value,
                description: this.$('#description').value,
                mode: this.$('#mode').value,
                singleInstance: this.$('#single-instance').checked,
                homepage: this.$('#homepage').checked,
                slugPrefix: this.$('#slug-prefix').value,
                layout,
                breadcrumbs,
                indexedByField: this.$('#indexed-by-field').value,
                composition,
                fields,
            });
        }
    }

    async #handleClick(e) {
        const btnRemove = e.target.closest('.btn-remove');
        if (btnRemove) {
            const dialog = document.querySelector('admin-confirm-dialog');
            const confirmed = await dialog.ask({
                title: `Delete template "${btnRemove.dataset.name}"?`,
                body: 'Pages that use this template stay on the published site, but you can no longer create new ones from it.',
                confirmLabel: 'Delete template',
                cancelLabel: 'Cancel',
                danger: true,
            });
            if (confirmed) {
                this.emit('template-delete', {slug: this.#slug, templateName: btnRemove.dataset.name});
            }
        }

        if (e.target.closest('#btn-republish-all')) {
            const dialog = document.querySelector('admin-confirm-dialog');
            const confirmed = await dialog.ask({
                title: 'Republish all pages?',
                body: 'Every published page is rendered again with the current templates and the changed files go live in one update. Page content is not changed.',
                confirmLabel: 'Republish all pages',
                cancelLabel: 'Cancel',
            });
            if (confirmed) {
                this.emit('site-republish', {slug: this.#slug});
            }
        }

        const btnEdit = e.target.closest('.btn-edit');
        if (btnEdit && await this.#discardTemplateChangesIfConfirmed()) {
            this.emit('template-edit-request', {slug: this.#slug, templateName: btnEdit.dataset.name});
        }

        if (e.target.closest('#btn-add-field')) {
            this.#addManualField();
        }
        if (e.target.closest('#btn-add-breadcrumb-override')) {
            this.#addBreadcrumbOverride();
        }
        if (e.target.closest('#btn-format-composition')) {
            this.#formatComposition();
        }
        if (e.target.closest('#btn-copy-slug')) {
            this.emit('clipboard-copy', {text: this.#client.slug, label: 'Slug'});
        }
        if (e.target.closest('#btn-copy-guid')) {
            this.emit('clipboard-copy', {text: this.#client.userCreationGuid, label: 'Registration code'});
        }

        const btnAddColumn = e.target.closest('.btn-add-column');
        if (btnAddColumn) {
            this.#updateColumns(btnAddColumn, (columns) => [...columns, emptyTableColumn()]);
        }
        const btnRemoveColumn = e.target.closest('.btn-remove-column');
        if (btnRemoveColumn) {
            this.#updateColumns(btnRemoveColumn, (columns, index) => columns.filter((_, i) => i !== index));
        }
        const btnMoveColumn = e.target.closest('.btn-move-column');
        if (btnMoveColumn) {
            const offset = btnMoveColumn.dataset.direction === 'up' ? -1 : 1;
            this.#updateColumns(btnMoveColumn, (columns, index) => {
                const target = index + offset;
                [columns[index], columns[target]] = [columns[target], columns[index]];
                return columns;
            });
        }

        const btnRemoveField = e.target.closest('.btn-remove-field');
        if (btnRemoveField) {
            this.#removeField(btnRemoveField.dataset.key);
        }
        const btnRemoveBreadcrumbOverride = e.target.closest('.btn-remove-breadcrumb-override');
        if (btnRemoveBreadcrumbOverride) {
            this.#removeBreadcrumbOverride(btnRemoveBreadcrumbOverride.dataset.id);
        }

        if (e.target.closest('#btn-scan-fields')) this.scanFields();
        if (e.target.closest('#btn-cancel-edit') && await this.#discardTemplateChangesIfConfirmed()) {
            this.exitEditMode();
        }

        if (e.target.closest('#btn-edit-client')) {
            const dialog = this.$('#edit-client-dialog');
            this.$('#edit-name').value = this.#client.name;
            this.$('#edit-repo').value = this.#client.repoName;
            this.$('#edit-domain').value = this.#client.domain || '';
            this.$('#edit-path').value = this.#client.basePath || '';
            this.$('#edit-guid').value = this.#client.userCreationGuid || '';
            this.showClientSettingsError(null);
            dialog.showModal();
        }

        if (e.target.closest('#btn-close-edit-client')) {
            this.closeClientSettings();
        }
    }

    closeClientSettings() {
        this.$('#edit-client-dialog')?.close();
    }

    /**
     * Shows why the backend rejected the client settings inside the settings dialog, since the
     * modal dialog covers the page and anything shown behind it.
     *
     * @param {string | null} message null hides the error
     * @returns {boolean} whether the dialog is open, so the message is on screen
     */
    showClientSettingsError(message) {
        const error = this.$('#edit-client-error');
        if (!error) return false;
        error.textContent = message || '';
        error.hidden = !message;
        return Boolean(this.$('#edit-client-dialog')?.open);
    }

    /**
     * Keeps the template named `name` in view in the template list: a filter it does not match is
     * cleared, so a template just opened or added never hides behind the filter.
     *
     * @param {string} name
     */
    revealTemplate(name) {
        const filter = this.$('#template-filter');
        if (!filter || templateMatchesFilter(name, filter.value)) return;
        filter.value = '';
        this.#renderTemplateList();
    }

    /**
     * Shows why the backend rejected the template, next to the form where it gets fixed.
     *
     * @param {string | null} message null hides the error
     */
    showTemplateError(message) {
        const error = this.$('#template-error');
        if (!error) return;
        error.textContent = message || '';
        error.hidden = !message;
        if (message) {
            error.scrollIntoView({block: 'nearest', behavior: 'smooth'});
        }
    }

    /**
     * @returns {boolean} true when the template form differs from the template as last loaded or
     *   saved (or from the empty form when adding a template), or the billing or one-off form from
     *   what it last opened
     */
    hasUnsavedChanges() {
        return this.#templateHasUnsavedChanges() || this.#billingHasUnsavedChanges() || this.#oneOffHasUnsavedChanges();
    }

    /** False before the component is rendered, when there is no billing card yet. */
    #billingHasUnsavedChanges() {
        return this.billingCard?.hasUnsavedChanges() ?? false;
    }

    /** False before the component is rendered, when there is no one-off section yet. */
    #oneOffHasUnsavedChanges() {
        return this.oneOffIncomes?.hasUnsavedChanges() ?? false;
    }

    #templateHasUnsavedChanges() {
        return this.#savedFormSnapshot !== null && this.#formSnapshot() !== this.#savedFormSnapshot;
    }

    /**
     * Asks whether to drop the unsaved changes of the template, billing and one-off forms, and
     * resets what was unsaved when every answer is yes. Nothing is reset when an answer is no.
     * Resolves true right away when nothing is unsaved.
     *
     * @returns {Promise<boolean>} true when the caller may go on and replace or leave the forms
     */
    async discardChangesIfConfirmed() {
        const templateDirty = this.#templateHasUnsavedChanges();
        const billingDirty = this.#billingHasUnsavedChanges();
        const oneOffDirty = this.#oneOffHasUnsavedChanges();
        if (templateDirty && !await this.#confirmDiscardTemplate()) {
            return false;
        }
        if (billingDirty && !await this.billingCard.confirmDiscard()) {
            return false;
        }
        if (oneOffDirty && !await this.oneOffIncomes.confirmDiscard()) {
            return false;
        }
        if (templateDirty) {
            this.exitEditMode();
        }
        if (billingDirty) {
            this.billingCard.discardChanges();
        }
        if (oneOffDirty) {
            this.oneOffIncomes.closeForm();
        }
        return true;
    }

    /** Like `discardChangesIfConfirmed`, for the template form only. */
    async #discardTemplateChangesIfConfirmed() {
        if (!this.#templateHasUnsavedChanges()) {
            return true;
        }
        const confirmed = await this.#confirmDiscardTemplate();
        if (confirmed) {
            this.exitEditMode();
        }
        return confirmed;
    }

    #confirmDiscardTemplate() {
        const name = this.#editingTemplateName;
        return confirmDiscard(
            name ? `Discard changes to "${name}"?` : 'Discard the new template?',
            name
                ? 'The changes you made to this template are not saved and will be lost.'
                : 'The template you started is not saved and will be lost.',
        );
    }

    /** @returns {HTMLElement} the recurring billing card, which owns the billing items and form */
    get billingCard() {
        return this.$('#billing-card');
    }

    /** @returns {HTMLElement} the client's one-off invoices, apart from the recurring billing card */
    get oneOffIncomes() {
        return this.$('#one-off-incomes');
    }

    /** @returns {string} slug of the client on screen, empty before one is shown */
    get currentSlug() {
        return this.#slug;
    }

    /**
     * Clears the client on screen and the template form when `slug` names another client, so
     * nothing of the previous client (its details, or a template open in the form) carries over
     * while the next one loads, or when it fails to load.
     *
     * @param {string} slug
     */
    prepareClient(slug) {
        if (slug === this.#slug || !this.$('#template-form')) {
            return;
        }
        this.#client = null;
        this.#slug = slug;
        this.exitEditMode();
        this.billingCard.reset(slug);
        this.oneOffIncomes.reset(slug);
        this.$('#template-filter').value = '';
        for (const id of ['#client-name-title', '#client-slug', '#client-repo', '#client-domain', '#client-path', '#client-guid']) {
            this.$(id).textContent = '';
        }
        this.$('#template-list').innerHTML = '';
    }

    /** @returns {string} the template form values, one entry per control in document order */
    #formSnapshot() {
        return JSON.stringify([...this.$('#template-form').elements]
            .filter((control) => control.tagName !== 'BUTTON' && !SNAPSHOT_IGNORED_IDS.has(control.id))
            .map((control) => (control.type === 'checkbox' ? control.checked : control.value)));
    }

    #markFormSaved() {
        this.#savedFormSnapshot = this.#formSnapshot();
    }

    #formatComposition() {
        const textarea = this.$('#composition-json');
        const {text, error} = formatCompositionText(textarea.value);
        if (error) {
            this.#showCompositionError(error);
            return;
        }
        replaceText(textarea, text);
        this.#showCompositionError(null);
    }

    /** Adds the field typed in the manual field input, or the fields of the selected preset. */
    #addManualField() {
        const preset = this.$('#manual-field-preset')?.value || CUSTOM_FIELD_PRESET;
        if (preset === CUSTOM_FIELD_PRESET) {
            this.#addFieldFromInput();
        } else {
            this.#addPresetField(preset);
        }
    }

    #addFieldFromInput() {
        const input = this.$('#manual-field-key');
        const key = String(input.value || '').trim();
        const error = validateManualFieldKey(key, this.#fieldConfigs);
        if (error) {
            input.setCustomValidity(error);
            input.reportValidity();
            return;
        }
        input.setCustomValidity('');
        this.#fieldConfigs.set(key, manualFieldConfig(key));
        input.value = '';
        this.#renderFieldConfigs();
    }

    /** @param {string | null} message */
    #showCompositionError(message) {
        const textarea = this.$('#composition-json');
        const error = this.$('#composition-json-error');
        textarea.setCustomValidity(message || '');
        error.textContent = message || '';
        error.hidden = !message;
        if (message) {
            this.$('#composition-editor').open = true;
            textarea.focus();
        }
    }

    #removeReservedAutoInjectedFields() {
        for (const key of [...this.#fieldConfigs.keys()]) {
            if (isReservedAutoInjectedTemplateFieldKey(key)) {
                this.#fieldConfigs.delete(key);
            }
        }
    }

    #renderBreadcrumbOverrides() {
        this.$('#breadcrumb-overrides-container').innerHTML = breadcrumbOverridesMarkup(this.#breadcrumbOverrides);
        this.$('#breadcrumb-overrides-empty-state').hidden = this.#breadcrumbOverrides.length > 0;
    }

    #syncBreadcrumbOverridesFromDOM() {
        this.#breadcrumbOverrides = readBreadcrumbOverrides(this.$$('.breadcrumb-override-row'));
    }

    #addBreadcrumbOverride() {
        this.#breadcrumbOverrides = [
            ...this.#breadcrumbOverrides,
            {id: this.#nextBreadcrumbOverrideId++, segment: '', label: '', href: ''}
        ];
        this.#renderBreadcrumbOverrides();
    }

    #removeBreadcrumbOverride(id) {
        const numericId = Number(id);
        this.#breadcrumbOverrides = this.#breadcrumbOverrides.filter((override) => override.id !== numericId);
        this.#renderBreadcrumbOverrides();
    }

    #validateBreadcrumbOverrides() {
        this.#syncBreadcrumbOverridesFromDOM();
        for (const input of this.$$('#breadcrumb-overrides-container [data-prop="segment"]')) {
            input.setCustomValidity('');
        }
        const duplicate = findDuplicateBreadcrumbOverride(this.#breadcrumbOverrides);
        if (duplicate) {
            const duplicateInput = this.$(`.breadcrumb-override-row[data-id="${duplicate.id}"] [data-prop="segment"]`);
            duplicateInput?.setCustomValidity(`Breadcrumb segment "${duplicate.segment}" is already configured.`);
            duplicateInput?.reportValidity();
            return false;
        }
        return true;
    }

    #addPresetField(presetName) {
        const fields = MANUAL_FIELD_PRESETS[presetName];
        const input = this.$('#manual-field-key');
        let added = false;
        for (const field of fields) {
            if (this.#fieldConfigs.has(field.key)) {
                continue;
            }
            this.#fieldConfigs.set(field.key, presetFieldConfig(field));
            added = true;
        }
        if (!added) {
            input.setCustomValidity(`Field "${fields[0].key}" already exists.`);
            input.reportValidity();
            return;
        }
        input.setCustomValidity('');
        this.#renderFieldConfigs();
    }

    #syncSlugField() {
        const mode = this.$('#mode')?.value;
        if (mode === RENDER_MODE.INTERNAL || mode === RENDER_MODE.INDEX_FRAGMENT) return;
        const prefix = (this.$('#slug-prefix')?.value || '').trim();
        if (prefix && !this.#fieldConfigs.has(SLUG_FIELD_KEY)) {
            this.#fieldConfigs.set(SLUG_FIELD_KEY, presetFieldConfig(MANUAL_FIELD_PRESETS.slug[0]));
            this.#renderFieldConfigs();
        }
    }

    #syncSlugPrefixVisibility() {
        const mode = this.$('#mode')?.value;
        const hideSlugPrefix = mode === RENDER_MODE.INTERNAL || mode === RENDER_MODE.INDEX_FRAGMENT;
        const slugPrefixGroup = this.$('#slug-prefix')?.closest('.form-group');
        if (slugPrefixGroup) {
            slugPrefixGroup.hidden = hideSlugPrefix;
        }
        if (hideSlugPrefix) {
            const slugField = this.#fieldConfigs.get(SLUG_FIELD_KEY);
            if (slugField?.manual) {
                this.#fieldConfigs.delete(SLUG_FIELD_KEY);
                this.#renderFieldConfigs();
            }
        }
    }

    /**
     * The homepage checkbox is only meaningful for standalone PAGE templates, since it is what
     * routes the template to index.html instead of a pages/ file.
     */
    #syncHomepageVisibility() {
        const mode = this.$('#mode')?.value;
        const showHomepage = mode === RENDER_MODE.PAGE;
        const homepageCheckbox = this.$('#homepage');
        const homepageGroup = homepageCheckbox?.closest('.form-group');
        if (homepageGroup) {
            homepageGroup.hidden = !showHomepage;
        }
        if (!showHomepage && homepageCheckbox?.checked) {
            homepageCheckbox.checked = false;
            this.#applyHomepageLock(false);
        }
    }

    /**
     * A homepage template must be a single instance with no slug prefix, so checking the box
     * checks and disables single instance and clears and disables the slug prefix. Unchecking it
     * re-enables both without changing single instance's value.
     *
     * @param {boolean} checked
     */
    #applyHomepageLock(checked) {
        const singleInstanceCheckbox = this.$('#single-instance');
        const slugPrefixInput = this.$('#slug-prefix');
        if (checked) {
            singleInstanceCheckbox.checked = true;
            singleInstanceCheckbox.disabled = true;
            slugPrefixInput.value = '';
            slugPrefixInput.disabled = true;
        } else {
            singleInstanceCheckbox.disabled = false;
            slugPrefixInput.disabled = false;
        }
    }

    #syncManualFieldControls() {
        const preset = this.$('#manual-field-preset');
        const input = this.$('#manual-field-key');
        const isCustom = preset.value === CUSTOM_FIELD_PRESET;
        input.disabled = !isCustom;
        input.value = isCustom ? input.value : '';
        input.setCustomValidity('');
        input.placeholder = isCustom ? 'field_key (e.g. custom_tag)' : 'Preset field selected';
    }

    #removeField(key) {
        this.#fieldConfigs.delete(key);
        this.#renderFieldConfigs();
    }

    /** @param {{ name: string, content: string, description: string, fields: import('../../shell/types/client.types.js').TemplateFieldType[] }} templateData */
    enterEditMode(templateData) {
        this.#editingTemplateName = templateData.name;

        // Hide name input, show static name
        this.$('#name-group').hidden = true;
        this.$('#name').removeAttribute('required');
        this.$('#editing-name-group').hidden = false;
        this.$('#editing-name').textContent = templateData.name;

        // Update form title and button
        this.$('#form-title').textContent = `Edit Template: ${templateData.name}`;
        this.$('#btn-submit-template').querySelector('span').textContent = 'Save Changes';
        this.$('#btn-cancel-edit').hidden = false;

        // Populate content and description
        this.$('#template-content').value = templateData.content;
        this.$('#description').value = templateData.description || '';
        this.$('#mode').value = templateData.mode || DEFAULT_RENDER_MODE;
        this.$('#single-instance').checked = Boolean(templateData.singleInstance);
        this.$('#slug-prefix').value = templateData.slugPrefix || '';
        this.$('#homepage').checked = Boolean(templateData.homepage);
        this.#applyHomepageLock(this.$('#homepage').checked);
        this.#layoutState = toLayoutEditorState(templateData.layout);
        this.#editingIndexedByField = templateData.indexedByField || '';
        this.$('#composition-json').value = formatCompositionJson(templateData.composition);
        this.$('#composition-editor').open = Boolean(templateData.composition);
        this.#showCompositionError(null);
        this.#breadcrumbOverrides = toBreadcrumbOverrides(templateData.breadcrumbs, () => this.#nextBreadcrumbOverrideId++);

        // Populate field configs from response
        const detectedKeys = extractTemplateKeys(templateData.content || '');
        this.#fieldConfigs.clear();
        for (const field of templateData.fields) {
            this.#fieldConfigs.set(field.key, toFieldConfig(field, detectedKeys));
        }
        this.#syncSlugPrefixVisibility();
        this.#syncSlugField();
        this.#syncHomepageVisibility();
        this.#renderFieldConfigs();
        this.#renderBreadcrumbOverrides();
        this.showTemplateError(null);
        this.revealTemplate(templateData.name);
        this.#renderTemplateList();
        this.#markFormSaved();

        // Scroll to form
        this.$('#form-title').scrollIntoView({behavior: 'smooth'});
    }

    exitEditMode() {
        this.#editingTemplateName = null;

        // Show name input, hide static name
        this.$('#name-group').hidden = false;
        this.$('#name').setAttribute('required', '');
        this.$('#editing-name-group').hidden = true;
        this.$('#editing-name').textContent = '';

        // Restore form title and button
        this.$('#form-title').textContent = 'Add Template';
        this.$('#btn-submit-template').querySelector('span').textContent = 'Add Template';
        this.$('#btn-cancel-edit').hidden = true;

        this.resetTemplateForm();
        this.#renderTemplateList();
    }

    /**
     * Clears every template form input and field setting. The driving adapter calls this once a
     * new template is saved, so a save the backend rejects keeps everything the user entered.
     */
    resetTemplateForm() {
        this.$('#template-form').reset();
        this.$('#mode').value = DEFAULT_RENDER_MODE;
        this.$('#single-instance').checked = false;
        this.$('#homepage').checked = false;
        this.#applyHomepageLock(false);
        this.#layoutState = toLayoutEditorState(null);
        this.#editingIndexedByField = '';
        this.#breadcrumbOverrides = [];
        this.$('#composition-editor').open = false;
        this.#showCompositionError(null);
        this.$('#manual-field-preset').value = CUSTOM_FIELD_PRESET;
        this.#syncManualFieldControls();
        this.#syncSlugPrefixVisibility();
        this.#syncHomepageVisibility();
        this.#fieldConfigs.clear();
        this.#renderFieldConfigs();
        this.#renderBreadcrumbOverrides();
        this.showTemplateError(null);
        this.#markFormSaved();
    }

    /**
     * Shows the loading overlay and disables the submit buttons, so a request cannot be sent twice
     * (the settings dialog sits above the overlay, so its button needs disabling too).
     *
     * @param {boolean} value
     */
    set loading(value) {
        const overlay = this.$('#loading-overlay');
        if (overlay) {
            overlay.hidden = !value;
            this.billingCard.submitting = value;
            this.oneOffIncomes.submitting = value;
            if (!value) {
                this.loadingText = DEFAULT_LOADING_TEXT;
            }
        }
        for (const button of this.$$('button[type="submit"]')) {
            button.disabled = value;
        }
    }

    /**
     * What the loading overlay says, for a request that takes longer than a plain load; the
     * overlay returns to {@link DEFAULT_LOADING_TEXT} once loading ends.
     *
     * @param {string} text
     */
    set loadingText(text) {
        const label = this.$('#loading-text');
        if (label) {
            label.textContent = text;
        }
    }

    /** @param {import('../../shell/types/client.types.js').ClientViewModel} client */
    set client(client) {
        if (this.#slug && client.slug !== this.#slug) {
            this.prepareClient(client.slug);
        }
        this.#client = client;
        this.render();
    }

    render() {
        if (!this.#client || !this.$('#client-name-title')) return;
        const {name, slug, repoName, basePath, domain, userCreationGuid, templates} = this.#client;
        this.#slug = slug;
        this.billingCard.slug = slug;
        this.oneOffIncomes.slug = slug;

        this.$('#client-name-title').textContent = name;
        this.$('#client-slug').textContent = slug;
        this.$('#client-repo').textContent = repoName;
        this.$('#client-domain').textContent = domain || 'Not set';
        this.$('#client-path').textContent = basePath || 'Root';
        this.$('#client-guid').innerHTML = `<code>${escapeHtml(userCreationGuid)}</code>`;
        this.$('#btn-copy-guid').hidden = !userCreationGuid;
        this.$('#template-filter').hidden = templates.length === 0;
        this.#renderTemplateList();
    }

    #renderTemplateList() {
        const list = this.$('#template-list');
        if (!this.#client || !list) return;
        renderTemplateList(list, this.#client.templates, {
            query: this.$('#template-filter').value,
            editingName: this.#editingTemplateName,
        });
    }
}

customElements.define('client-detail', ClientDetailComponent);
